import React from 'react';
import { Button, useTheme, alpha } from '@mui/material';
import { motion } from 'framer-motion';

/**
 * GlowButton - Enhanced button component with glow effects
 * Provides modern styling with hover animations and glow effects
 */
const GlowButton = ({
  children, 
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  disabled = false,
  glowIntensity = 0.3,
  sx = {},
  ...props 
}) => {
  const theme = useTheme();
  
  /**
   * Get glow color based on button color
   */
  const getGlowColor = () => {
    switch (color) {
      case 'primary':
        return theme.palette.primary.main;
      case 'secondary':
        return theme.palette.secondary.main;
      case 'success':
        return theme.palette.success.main;
      case 'error':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'info':
        return theme.palette.info.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const glowColor = getGlowColor();
  
  /**
   * Animation variants
   */
  const buttonVariants = {
    initial: {
      scale: 1,
      boxShadow: `0 0 0px ${alpha(glowColor, 0)}`,
    },
    hover: {
      scale: 1.02,
      boxShadow: `0 0 20px ${alpha(glowColor, glowIntensity)}, 0 0 40px ${alpha(glowColor, glowIntensity * 0.5)}`,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 25,
        duration: 0.3
      }
    },
    tap: {
      scale: 0.98,
      boxShadow: `0 0 15px ${alpha(glowColor, glowIntensity * 1.2)}`,
      transition: {
        type: 'spring',
        stiffness: 600,
        damping: 30,
        duration: 0.1
      }
    },
    disabled: {
      scale: 1,
      boxShadow: `0 0 0px ${alpha(glowColor, 0)}`,
      opacity: 0.6,
    }
  };
  
  /**
   * Get button styles based on variant and color
   */
  const getButtonStyles = () => {
    const baseStyles = {
      borderRadius: 2,
      textTransform: 'none',
      fontWeight: 600,
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: `linear-gradient(90deg, transparent, ${alpha('#fff', 0.2)}, transparent)`,
        transition: 'left 0.5s ease',
        zIndex: 1,
      },
      '&:hover::before': {
        left: '100%',
      },
      '& .MuiButton-startIcon, & .MuiButton-endIcon': {
        transition: 'transform 0.3s ease',
      },
      '&:hover .MuiButton-startIcon': {
        transform: 'translateX(-2px)',
      },
      '&:hover .MuiButton-endIcon': {
        transform: 'translateX(2px)',
      },
    };

    if (variant === 'contained') {
      return {
        ...baseStyles,
        background: `linear-gradient(135deg, ${theme.palette[color].main}, ${theme.palette[color].dark})`,
        '&:hover': {
          background: `linear-gradient(135deg, ${theme.palette[color].dark}, ${theme.palette[color].main})`,
          boxShadow: `0 4px 20px ${alpha(glowColor, 0.4)}`,
        },
        '&:active': {
          background: `linear-gradient(135deg, ${theme.palette[color].dark}, ${alpha(theme.palette[color].dark, 0.8)})`,
        }
      };
    }

    if (variant === 'outlined') {
      return {
        ...baseStyles,
        border: `2px solid ${theme.palette[color].main}`,
        color: theme.palette[color].main,
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: alpha(theme.palette[color].main, 0.08),
          borderColor: theme.palette[color].dark,
          boxShadow: `0 0 15px ${alpha(glowColor, 0.3)}`,
        },
        '&:active': {
          backgroundColor: alpha(theme.palette[color].main, 0.12),
        }
      };
    }

    if (variant === 'text') {
      return {
        ...baseStyles,
        color: theme.palette[color].main,
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: alpha(theme.palette[color].main, 0.08),
          boxShadow: `0 0 10px ${alpha(glowColor, 0.2)}`,
        },
        '&:active': {
          backgroundColor: alpha(theme.palette[color].main, 0.12),
        }
      };
    }

    return baseStyles;
  };
  
  return (
    <Button
      component={motion.button}
      variants={buttonVariants}
      initial="initial"
      whileHover={!disabled ? "hover" : undefined}
      whileTap={!disabled ? "tap" : undefined}
      animate={disabled ? "disabled" : "initial"}
      variant={variant}
      color={color}
      size={size}
      disabled={disabled}
      sx={{
        ...getButtonStyles(),
        ...sx,
      }}
      {...props}
    >
            {children}
      </Button>
  );
};

export default GlowButton; 